import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms"
import { ModalService } from "../../services/modal.service"
import { ModalComponent } from "./modal.interface"
import { Component, Inject } from "@angular/core"
import { ApiService } from "../../services/api.service"
import { TranslateService } from "../../services/translate.service"
import { AuthService } from "../../services/auth.service"
import { DialogButtonComponent } from "../buttons/dialog-button.component"
import { CommonModule } from "@angular/common"

@Component({
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DialogButtonComponent,
    CommonModule,
  ],
  template: `
    <div class="bg-theme-surface rounded-2xl shadow-xl max-w-lg w-80 text-theme-text">
      <div class="p-6" [formGroup]="emailForm">
        <h2 class="text-xl font-bold mb-4">{{ data.title }}</h2>
        <p *ngIf="data.message">{{ data.message }}</p>
        <input
          type="email"
          formControlName="email"
          [placeholder]="getString('Ange e-post')"
          class="mt-4 p-2 w-full border text-theme-text bg-theme-surface2"
        />
        <div *ngIf="emailControl.invalid && emailControl.touched" class="text-theme-warning mt-2">
          {{getString('Ogiltig e-post')}}
        </div>

        <div class="mt-4" *ngIf="!isKioskMode()">
          <label class="inline-flex items-center">
            <input type="checkbox" formControlName="saveEmail" class="mr-2" />
            {{ getString('Spara e-post') }}
          </label>
        </div>
        <div class="mt-4 flex justify-end gap-2">
          <app-dialog-button
            [text]="data.abortText"
            (onClick)="close()">
          </app-dialog-button>
          <app-dialog-button
            [text]="data.confirmText"
            [disabled]="emailControl.invalid || isWorking"
            (onClick)="confirm()"
            variant="primary">
          </app-dialog-button>
        </div>
        <div *ngIf="isWorking" class="mt-4 text-theme-text3">{{ getString("Skickar...") }}</div>
        <div *ngIf="errorMsg" class="mt-4 text-theme-warning">{{ errorMsg }}</div>
      </div>
    </div>
  `
})
export class ReceiptEmailModalComponent implements ModalComponent {
  modalData?: any
  emailForm!: FormGroup
  isWorking = false
  errorMsg?: string

  constructor(
    private modalService: ModalService,
    public api: ApiService,
    private translate: TranslateService,
    private auth: AuthService,
    @Inject('MODAL_DATA') public data: {
      title: string
      message: string
      confirmText: string
      abortText: string
      action: string
      venueId: string
      receiptKey?: string
      voucherId?: string
      postId?: string,
      mailRecipient?: string
    },
  ) {
    this.modalData = data

    this.emailForm = new FormGroup({
      email: new FormControl(data.mailRecipient || '', {
        nonNullable: true,
        validators: [Validators.required, Validators.email]
      }),
      saveEmail: new FormControl(data.mailRecipient !== undefined)

    }, {
      updateOn: 'change'
    })
  }

  get emailControl() {
    return this.emailForm.controls["email"]!
  }

  close() {
    this.modalService.close()
  }

  getString(s: string) {
    return this.translate.getString(s)
  }

  isKioskMode(): boolean {
    return this.auth.isKioskMode();
  }

  confirm() {
    if (this.emailControl.invalid) {
      return
    }
    this.isWorking = true
    this.errorMsg = undefined
    const email = this.emailControl.value
    const saveEmail = this.emailForm.controls["saveEmail"]!.value
    switch (this.data.action) {
      case 'send_receipt':
        this.api.sendReceiptPdf(this.data.venueId, this.data.receiptKey!, email, saveEmail).subscribe({
          next: () => {
            this.isWorking = false
            this.close()
          },
          error: err => {
            this.isWorking = false
            this.errorMsg = err?.message || this.getString("Något gick fel")
          }
        })
        break
      case 'send_voucher':
        this.api.sendVoucherPdf(this.data.venueId, this.data.voucherId!, email).subscribe({
          next: () => {
            this.isWorking = false
            this.close()
          },
          error: err => {
            this.isWorking = false
            this.errorMsg = err?.message || this.getString("Något gick fel")
          }
        })
        break
      case 'send_all_vouchers':
        this.api.sendAllVoucherPdfs(this.data.venueId, this.data.receiptKey!, email).subscribe({
          next: () => {
            this.isWorking = false
            this.close()
          },
          error: err => {
            this.isWorking = false
            this.errorMsg = err?.message || this.getString("Något gick fel")
          }
        })
        break
      case 'send_giftcard':
        this.api.sendGiftcard(this.data.venueId, this.data.postId!, email).subscribe({
          next: () => {
            this.isWorking = false
            this.close()
          },
          error: err => {
            this.isWorking = false
            this.errorMsg = err?.message || this.getString("Något gick fel")
          }
        })
        break
      default:
        this.isWorking = false
        this.close()
    }
  }
}
